/** How many FastAPI 422 validation messages are shown at most. */
const MAX_VALIDATION_MESSAGES = 3;

/** Longest error text shown on screen. */
const MAX_LENGTH = 240;

/** One line of screen text: control characters become spaces, then it is capped. */
function clean(s: string): string {
  return s.replace(/[\u0000-\u001f\u007f]/g, " ").trim().slice(0, MAX_LENGTH);
}

/** A bare code ("no_ch_coverage") is for the client, not the user. */
function isCode(s: string): boolean {
  return /^[\w.:-]+$/.test(s);
}

/** The `{status, body}` an ApiError (lib/api.ts) carries; duck-typed so this file stays pure. */
function apiFailure(e: unknown): { status: number; body: string } | null {
  if (!(e instanceof Error)) return null;
  const { status, body } = e as Error & { status?: unknown; body?: unknown };
  return typeof status === "number" && typeof body === "string" ? { status, body } : null;
}

/**
 * The one place an API error becomes on-screen text. A 4xx whose body carries
 * a plain-sentence `detail` (a validation or permission message written for
 * the user) shows that sentence; a FastAPI 422 list detail shows up to three
 * of its `msg` sentences (never the `loc` paths or `type` codes). Anything
 * else — a 5xx, a non-JSON body, a structured detail, a bare code, a network
 * failure — shows `fallback`. The raw `ApiError.message` ("API {status} on
 * {path}: {body}"), the body and the status code never reach the screen.
 */
export function parseApiError(
  e: unknown,
  fallback = "Something went wrong. Try again later.",
): string {
  const failure = apiFailure(e);
  if (!failure || failure.status >= 500) return fallback;
  let detail: unknown;
  try {
    detail = (JSON.parse(failure.body) as { detail?: unknown }).detail;
  } catch {
    return fallback;
  }
  if (typeof detail === "string") {
    const text = clean(detail);
    return text && !isCode(text) ? text : fallback;
  }
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d && typeof (d as { msg?: unknown }).msg === "string" ? (d as { msg: string }).msg.trim() : ""))
      .filter((m) => m && !isCode(m))
      .slice(0, MAX_VALIDATION_MESSAGES);
    return msgs.length ? clean(msgs.map((m) => (/[.!?]$/.test(m) ? m : `${m}.`)).join(" ")) : fallback;
  }
  return fallback;
}
